package essntl.components

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.width
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.dp
import com.essntl.toastmanager.Toast
import com.essntl.toastmanager.ToastManager
import com.essntl.uikit.components.chat.contact.ContactUiState
import com.essntl.uikit.components.chat.conversation.ConversationType
import com.essntl.uikit.components.chat.conversation.overviewcard.ConversationOverviewUiState
import com.essntl.uikit.components.chat.conversation.overviewcard.EsConversationOverviewCard
import com.essntl.uikit.components.toaster.EsToaster
import com.essntl.uikit.components.toaster.EsToasterState
import com.essntl.uikit.Platform
import com.essntl.uikit.currentPlatform
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@Composable
fun EsToastContainer(
    toastManager: ToastManager,
    modifier: Modifier = Modifier,
) {
    val scope = rememberCoroutineScope()

    val toastList = remember {
        mutableStateListOf<Toast>()
    }

    LaunchedEffect(Unit) {
        toastManager.toastFlow.collect { toast ->
            toastList.removeAll { it.id == toast.id }
            if ((currentPlatform == Platform.Android || currentPlatform == Platform.Ios) && toast is Toast.Message)
                return@collect
            toastList.add(toast)
        }
    }

    Box(
        modifier = modifier,
    ) {
        toastList.forEachIndexed { index, toast ->
            val reverseIndex = toastList.lastIndex - index

            key(toast.id) {
                LaunchedEffect(reverseIndex) {
                    if (reverseIndex > 0)
                        return@LaunchedEffect

                    delay(ToastManager.TOAST_DURATION_MILLIS)
                    delay(ToastManager.TOAST_DURATION_MILLIS)
                    toastList.removeAll { it.id == toast.id }
                }

                val enterAnimation = remember { Animatable(0f) }

                LaunchedEffect(Unit) {
                    enterAnimation.animateTo(1f)
                }

                val reverseIndexAnimatable = remember { Animatable(reverseIndex.toFloat()) }

                LaunchedEffect(reverseIndex) {
                    reverseIndexAnimatable.animateTo(
                        targetValue = reverseIndex.toFloat(),
                        animationSpec = tween(
                            durationMillis = 200,
                        ),
                    )
                }

                Box(
                    modifier = Modifier
                        .graphicsLayer {
                            transformOrigin = TransformOrigin.Center
                            translationX = size.width * (1f - enterAnimation.value)
                            translationY = (-reverseIndexAnimatable.value * 8).dp.toPx()
                            scaleX = 1f - (reverseIndexAnimatable.value * 0.05f)
                            scaleY = 1f - (reverseIndexAnimatable.value * 0.05f)
                            alpha = (1f - (reverseIndexAnimatable.value * 0.2f)) * enterAnimation.value
                        },
                ) {
                    when (toast) {
                        is Toast.Default ->
                            EsToaster(
                                state = when (toast.state) {
                                    Toast.Default.State.Success -> EsToasterState.Success
                                    Toast.Default.State.Warning -> EsToasterState.Warning
                                    Toast.Default.State.Error -> EsToasterState.Error
                                    Toast.Default.State.Information -> EsToasterState.Information
                                },
                                title = toast.title,
                                description = toast.description,
                                modifier = Modifier,
                            )

                        is Toast.Message -> {
                            val conversationOverviewUiState = remember(toast) {
                                toast.conversationOverviewUiState
                            }

                            EsConversationOverviewCard(
                                uiState = conversationOverviewUiState,
                                isSelected = false,
                                onClick = { _, _ ->
                                    scope.launch {
                                        toastManager.clickToast(toast)
                                    }
                                },
                                enabled = reverseIndex == 0,
                                modifier = Modifier
                                    .width(356.dp),
                            )
                        }
                    }
                }
            }
        }
    }
}

val Toast.Message.conversationOverviewUiState: ConversationOverviewUiState
    get() =
        ConversationOverviewUiState(
            conversationId = conversationId,
            conversationType = type?.let(ConversationType::valueOf),
            contact = ContactUiState(
                name = senderName,
                phoneNumber = null,
                type = senderType?.let(ContactUiState.ContactType::valueOf),
                isOnline = true,
            ),
            lastMessage = message,
            isCurrentUserSender = false,
            unreadMessages = unreadMessages,
            dateTime = dateTime,
            lastMessageStatus = null,
            type = ConversationOverviewUiState.ChatType.Unknown,
            participants = null,
        )